import { supabase } from "@/integrations/supabase/client";
import { Bundle } from "@/types/bundle";
import { toast } from "@/components/ui/use-toast";

export async function getBundles() {
  const { data, error } = await supabase
    .from('bundles')
    .select('*')
    .order('start_date', { ascending: false });

  if (error) {
    console.error('Error fetching bundles:', error);
    return [];
  }

  return data as Bundle[];
}

export async function getCurrentBundle() {
  const { data, error } = await supabase
    .from('bundles')
    .select('*')
    .eq('status', 'active')
    .eq('bundle_type', 'temporal')
    .maybeSingle();

  if (error) {
    console.error('Error fetching active temporal bundle:', error);
    return null;
  }

  return data as Bundle | null;
}

export async function getPermanentBundles() {
  const { data, error } = await supabase
    .from('bundles')
    .select('*')
    .eq('status', 'active')
    .eq('bundle_type', 'permanent')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching permanent bundles:', error);
    return [];
  }

  return data as Bundle[];
}

export async function getUpcomingBundle() {
  const { data, error } = await supabase
    .from('bundles')
    .select('*')
    .eq('status', 'upcoming')
    .eq('bundle_type', 'temporal')
    .maybeSingle();

  if (error) {
    console.error('Error fetching upcoming temporal bundle:', error);
    return null;
  }

  return data as Bundle | null;
}

export async function getExpiredBundles() {
  const { data, error } = await supabase
    .from('bundles')
    .select('*')
    .eq('status', 'expired')
    .eq('bundle_type', 'temporal')
    .order('end_date', { ascending: false });

  if (error) {
    console.error('Error fetching expired temporal bundles:', error);
    return [];
  }

  return data as Bundle[];
}

export async function checkBundlePurchase(bundleId: number) {
  const { data: session } = await supabase.auth.getSession();
  if (!session?.session?.user?.id) return false;

  try {
    // Get the user's current active profile or primary profile
    let profileData = null;
    
    // Check if user is admin from user_accounts table
    const { data: accountData, error: accountError } = await supabase
      .from('user_accounts')
      .select('is_admin')
      .eq('user_id', session.session.user.id)
      .maybeSingle();
      
    // If the user is an admin, grant access regardless of purchase status
    if (!accountError && accountData?.is_admin) {
      return true;
    }
    
    // Check for active purchase in bundle_purchases
    const { data: bundlePurchases, error: bundlePurchaseError } = await supabase
      .from('bundle_purchases')
      .select('*')
      .eq('bundle_id', bundleId)
      .eq('user_id', session.session.user.id);

    if (bundlePurchaseError) {
      console.error('Error checking bundle_purchases:', bundlePurchaseError);
      throw bundlePurchaseError;
    }

    // Check if any purchase has access_enabled
    const activeAccess = bundlePurchases?.find(purchase => purchase.access_enabled === true);
    
    if (activeAccess) {
      // For upcoming bundles, check if they should now be unlocked
      const { data: bundle, error: bundleError } = await supabase
        .from('bundles')
        .select('status, unlock_date, start_date')
        .eq('id', bundleId)
        .single();
      
      if (!bundleError && bundle) {
        // If it's an upcoming bundle, check if it should be unlocked
        if (bundle.status === 'upcoming') {
          const unlockDate = bundle.unlock_date ? new Date(bundle.unlock_date) : bundle.start_date ? new Date(bundle.start_date) : null;
          const isUnlocked = unlockDate ? new Date() >= unlockDate : false;
          
          // If the bundle is now unlocked and it was previously upcoming, return true for full access
          if (isUnlocked) {
            return true;
          }
          
          // For upcoming bundles that aren't unlocked yet, check if user has early access book
          return activeAccess.early_access_book_id !== null;
        }
      }
      
      return true;
    }

    // Also check purchases table as fallback (old method)
    const { data: purchaseData, error: purchaseError } = await supabase
      .from('purchases')
      .select('*')
      .eq('bundle_id', bundleId)
      .eq('user_id', session.session.user.id);

    if (purchaseError) {
      console.error('Error checking purchases:', purchaseError);
      throw purchaseError;
    }

    if (purchaseData && purchaseData.length > 0) {
      // If we found a purchase record but no bundle_purchase record with access, let's create one
      if (!bundlePurchases || bundlePurchases.length === 0) {
        const { error: createError } = await supabase
          .from('bundle_purchases')
          .insert({
            bundle_id: bundleId,
            user_id: session.session.user.id,
            access_enabled: true,
            status: 'regular'
          });
        
        if (createError) {
          console.error('Error creating missing bundle_purchase record:', createError);
        }
      } else if (bundlePurchases.length > 0 && !activeAccess) {
        // We have purchase records but none with access_enabled = true
        const { error: updateError } = await supabase
          .from('bundle_purchases')
          .update({ access_enabled: true })
          .eq('id', bundlePurchases[0].id);
          
        if (updateError) {
          console.error('Error updating bundle_purchase access:', updateError);
        }
      }
      
      return true;
    }

    return false;
    
  } catch (e) {
    console.error('Error in checkBundlePurchase:', e);
    return false;
  }
}

export async function purchaseBundle(bundleId: number, earlyAccessBookId?: number) {
  const { data: session } = await supabase.auth.getSession();
  if (!session?.session?.user?.id) {
    toast({
      title: "Authentication Required",
      description: "Please sign in to purchase bundles",
      variant: "destructive",
    });
    return null;
  }

  try {
    // First get the bundle to determine the status
    const { data: bundle, error: bundleError } = await supabase
      .from('bundles')
      .select('*')
      .eq('id', bundleId)
      .single();

    if (bundleError) {
      throw bundleError;
    }

    // First check if they already have access
    const { data: existingPurchase, error: existingError } = await supabase
      .from('bundle_purchases')
      .select('*')
      .eq('bundle_id', bundleId)
      .eq('user_id', session.session.user.id)
      .maybeSingle();

    if (existingError) {
      throw existingError;
    }

    let purchase;
    if (existingPurchase) {
      // If they already have a purchase, update it to enable access and set early access book
      const { data: updatedPurchase, error: updateError } = await supabase
        .from('bundle_purchases')
        .update({ 
          access_enabled: true,
          early_access_book_id: earlyAccessBookId,
          status: bundle.status === 'upcoming' ? 'upcoming' : 'regular'
        })
        .eq('id', existingPurchase.id)
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }
      purchase = updatedPurchase;
    } else {
      // Create new purchase with early access book
      const { data: newPurchase, error: purchaseError } = await supabase
        .from('bundle_purchases')
        .insert({
          bundle_id: bundleId,
          user_id: session.session.user.id,
          early_access_book_id: earlyAccessBookId,
          status: bundle.status === 'upcoming' ? 'upcoming' : 'regular',
          access_enabled: true
        })
        .select()
        .single();

      if (purchaseError) {
        throw purchaseError;
      }
      purchase = newPurchase;
    }

    // Record the payment
    if (bundle) {
      const price = bundle.status === 'upcoming' ? bundle.early_access_price : bundle.price;
      const { error: paymentError } = await supabase
        .from('purchases')
        .insert({
          bundle_id: bundleId,
          user_id: session.session.user.id,
          amount_paid: price
        });

      if (paymentError) {
        throw paymentError;
      }
    }

    return purchase;

  } catch (error) {
    toast({
      title: "Purchase Failed",
      description: error instanceof Error ? error.message : "Please try again",
      variant: "destructive",
    });
    throw error;
  }
}

export async function getEarlyAccessBook(bundleId: number) {
  const { data: session } = await supabase.auth.getSession();
  if (!session?.session?.user?.id) return null;

  const { data, error } = await supabase
    .from('bundle_purchases')
    .select('early_access_book_id')
    .eq('bundle_id', bundleId)
    .eq('user_id', session.session.user.id)
    .maybeSingle();

  if (error) {
    console.error('Error fetching early access book:', error);
    return null;
  }

  return data?.early_access_book_id || null;
}

export async function selectEarlyAccessBook(bundleId: number, bookId: number) {
  const { data: session } = await supabase.auth.getSession();
  if (!session?.session?.user?.id) return false;

  const { error } = await supabase
    .from('bundle_purchases')
    .update({ early_access_book_id: bookId })
    .eq('bundle_id', bundleId)
    .eq('user_id', session.session.user.id);

  if (error) {
    console.error('Failed to select early access book:', error);
    return false;
  }

  return true;
}
